import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@components/modals/confirm-modal/confirm-modal';
import { ModalV2 } from '@components/modals/modal-v2';
import { isMobileImplicitDismiss, type ModalCloseSource } from '@components/modals/modal-v2/modal-v2.type';
import { CompanyClubContext } from '@contexts/company-club/company-club-context';

import { ACTION_MODALS } from './constants';
import { ModalTypes } from './modal.type';
import { ModalContext } from './modal-context';

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const {
    clubState: { id: clubId },
  } = useContext(CompanyClubContext);
  const [modalClubId, setModalClubId] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [currentModalType, setCurrentModalType] = useState<ModalTypes>('');
  const [preventExit, setPreventExit] = useState<boolean>(false);

  const toggleModal = useCallback(
    ({ type, isOpen }: { type: ModalTypes; isOpen?: boolean }) => {
      setCurrentModalType(type);
      const shouldOpen = isOpen ?? !isModalOpen;
      setIsModalOpen(shouldOpen);
      if (shouldOpen) setModalClubId(clubId);

      // A close from any path (including the venue change below) must not leave the exit
      // confirmation armed for the next modal.
      if (!shouldOpen) {
        setPreventExit(false);
        setIsConfirmModalOpen(false);
      }
    },
    [isModalOpen, clubId]
  );

  // The modals live above the router, so they outlive a venue change (Back to select-venue, a
  // redirect). Their forms read the venue they were opened in; rendering them once it is gone
  // crashes on the emptied club state, so they must disappear in the same render.
  const isModalForCurrentClub = modalClubId === clubId;

  useEffect(() => {
    if (isModalOpen && !isModalForCurrentClub) toggleModal({ type: '', isOpen: false });
  }, [isModalOpen, isModalForCurrentClub, toggleModal]);

  const handleCloseModal = useCallback(
    (source?: ModalCloseSource) => {
      if (preventExit && !isMobileImplicitDismiss(source)) {
        setIsConfirmModalOpen(true);
      } else {
        toggleModal({ type: '', isOpen: false });
      }
    },
    [preventExit, toggleModal]
  );

  const handleConfirmModal = () => {
    setPreventExit(false);
    setIsConfirmModalOpen(false);
    toggleModal({ type: '', isOpen: false });
  };

  const ModalComponent = useCallback(() => {
    if (!currentModalType || !isModalForCurrentClub) return null;

    return ACTION_MODALS[currentModalType].component();
  }, [currentModalType, isModalForCurrentClub]);

  const contextValue = useMemo(
    () => ({ currentModalType, toggleModal, setPreventExit, handleCloseModal }),
    [currentModalType, toggleModal, setPreventExit, handleCloseModal]
  );

  return (
    <ModalContext.Provider value={contextValue}>
      <ModalV2 isOpen={isModalOpen && isModalForCurrentClub} onClose={handleCloseModal} variant='lg'>
        <ModalComponent />
        <ConfirmModal
          isOpen={isConfirmModalOpen}
          title={t('offers:admissionModalConfirmTitle')}
          handleClickConfirm={handleConfirmModal}
          handleClose={() => setIsConfirmModalOpen(false)}
        />
      </ModalV2>
      {children}
    </ModalContext.Provider>
  );
};
